'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Sparkles, FileText, Layout, Edit3, Download, ChevronRight, Check, ArrowRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const demoSteps = [
  {
    id: 'request',
    label: 'Your Request',
    icon: Sparkles,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
  },
  {
    id: 'brief',
    label: 'AI Brief',
    icon: FileText,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
  },
  {
    id: 'concepts',
    label: '3 Concepts',
    icon: Layout,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
  },
  {
    id: 'editor',
    label: 'Layer Editor',
    icon: Edit3,
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
  },
  {
    id: 'export',
    label: 'Export',
    icon: Download,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
  },
];

function RequestPanel() {
  const [typed, setTyped] = useState('');
  const fullText = 'Create a grand opening campaign for my nail studio in Oslo. Modern, pink and gold theme.';

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      if (i < fullText?.length) {
        setTyped(fullText?.slice(0, i + 1));
        i++;
      } else {
        clearInterval(t);
      }
    }, 35);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl bg-surface-3 border border-border p-4">
        <div className="text-xs text-slate-400 mb-2 font-medium">Your request</div>
        <p className="text-sm text-slate-200 leading-relaxed min-h-[60px]">
          {typed}
          <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse align-middle" />
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {['Nail Studio', 'Oslo', 'Grand Opening', 'Pink & Gold']?.map((tag) => (
          <span key={tag} className="px-2.5 py-1 rounded-lg bg-primary/10 border border-primary/20 text-xs text-primary font-medium">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function BriefPanel() {
  const briefItems = [
    { label: 'Business', value: 'Nail Studio — Oslo' },
    { label: 'Goal', value: 'Grand Opening Awareness' },
    { label: 'Tone', value: 'Luxurious, Modern, Feminine' },
    { label: 'Colors', value: 'Pink #F472B6 · Gold #D4AF37' },
    { label: 'Outputs', value: 'Poster · Story · Flyer · QR' },
    { label: 'Language', value: 'Norwegian + English' },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
          <Check size={10} className="text-blue-400" />
        </div>
        <span className="text-xs font-semibold text-blue-400">AI Brief Generated</span>
      </div>
      <div className="rounded-2xl bg-surface-3 border border-border overflow-hidden">
        {briefItems?.map((item, i) => (
          <div key={item?.label} className={`flex items-center justify-between px-4 py-2.5 text-xs ${i < briefItems?.length - 1 ? 'border-b border-border' : ''}`}>
            <span className="text-slate-400 font-medium">{item?.label}</span>
            <span className="text-slate-200 font-semibold text-right max-w-[55%]">{item?.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConceptsPanel() {
  const [selected, setSelected] = useState(1);
  const concepts = [
    { id: 0, name: 'Concept A', style: 'Minimalist', color: 'from-pink-500/30 to-rose-600/20', accent: 'border-pink-400/40' },
    { id: 1, name: 'Concept B', style: 'Luxurious', color: 'from-amber-500/30 to-yellow-600/20', accent: 'border-amber-400/40', recommended: true },
    { id: 2, name: 'Concept C', style: 'Bold Modern', color: 'from-purple-500/30 to-violet-600/20', accent: 'border-purple-400/40' },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-slate-400 font-medium">Choose your concept</div>
      <div className="grid grid-cols-3 gap-2">
        {concepts?.map((c) => (
          <button
            key={c?.id}
            onClick={() => setSelected(c?.id)}
            className={`relative rounded-xl overflow-hidden border-2 transition-all duration-200 ${
              selected === c?.id ? c?.accent + ' scale-[1.03]' : 'border-border'
            }`}
          >
            {c?.recommended && (
              <div className="absolute top-1 left-1 z-10 bg-primary text-primary-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                ✦ Best
              </div>
            )}
            <div className={`h-20 bg-gradient-to-br ${c?.color} flex flex-col items-center justify-center gap-1 p-2`}>
              <div className="w-8 h-1.5 rounded bg-white/30" />
              <div className="w-6 h-1 rounded bg-white/20" />
              <div className="w-10 h-3 rounded bg-white/15 mt-1" />
            </div>
            <div className="p-1.5 bg-card">
              <div className="text-[10px] font-bold text-foreground">{c?.name}</div>
              <div className="text-[9px] text-slate-400">{c?.style}</div>
            </div>
          </button>
        ))}
      </div>
      {selected !== null && (
        <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
          <Check size={12} />
          Concept {['A','B','C']?.[selected]} selected — opening editor
        </div>
      )}
    </div>
  );
}

function EditorPanel() {
  const layers = [
    { name: 'Background', locked: true, color: 'bg-slate-500/30' },
    { name: 'Hero Image', locked: false, color: 'bg-pink-500/30' },
    { name: 'Logo', locked: false, color: 'bg-amber-500/30' },
    { name: 'Headline', locked: false, color: 'bg-blue-500/30' },
    { name: 'CTA Button', locked: false, color: 'bg-green-500/30' },
    { name: 'QR Code', locked: false, color: 'bg-purple-500/30' },
  ];

  return (
    <div className="flex gap-3">
      {/* Canvas preview */}
      <div className="flex-1 rounded-xl bg-gradient-to-br from-pink-500/20 to-amber-500/10 border border-border flex flex-col items-center justify-center p-3 gap-2 min-h-[140px]">
        <div className="w-12 h-12 rounded-full bg-pink-400/30 border border-pink-300/20" />
        <div className="w-20 h-2 rounded bg-white/30" />
        <div className="w-14 h-1.5 rounded bg-white/20" />
        <div className="w-16 h-5 rounded-lg bg-amber-400/40 flex items-center justify-center">
          <div className="w-8 h-1 rounded bg-white/50" />
        </div>
      </div>
      {/* Layers panel */}
      <div className="w-28 flex flex-col gap-1">
        <div className="text-[10px] text-slate-400 font-semibold mb-1">Layers</div>
        {layers?.map((layer) => (
          <div key={layer?.name} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface-3 border border-border">
            <div className={`w-2 h-2 rounded-sm ${layer?.color} flex-shrink-0`} />
            <span className="text-[10px] text-slate-300 truncate">{layer?.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExportPanel() {
  const formats = [
    { label: 'PNG', desc: 'Social media', icon: '🖼', ready: true },
    { label: 'PDF', desc: 'Print-ready', icon: '📄', ready: true },
    { label: 'MP4', desc: 'Video story', icon: '🎬', ready: false },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-slate-400 font-medium">Export your design</div>
      <div className="grid grid-cols-3 gap-2">
        {formats?.map((f) => (
          <div
            key={f?.label}
            className={`rounded-xl border p-3 flex flex-col items-center gap-1.5 text-center ${
              f?.ready ? 'border-primary/30 bg-primary/5' : 'border-border bg-card opacity-60'
            }`}
          >
            <span className="text-xl">{f?.icon}</span>
            <span className="text-xs font-bold text-foreground">{f?.label}</span>
            <span className="text-[10px] text-slate-400">{f?.desc}</span>
            {f?.ready && (
              <div className="flex items-center gap-1 text-[10px] text-primary font-semibold">
                <Check size={9} />
                Ready
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
        <Check size={14} className="text-green-400 flex-shrink-0" />
        <span className="text-xs text-green-300 font-medium">Design complete — 4 files ready to download</span>
      </div>
    </div>
  );
}

const stepPanels = [RequestPanel, BriefPanel, ConceptsPanel, EditorPanel, ExportPanel];

export default function InteractiveDemoSection() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((s) => (s + 1) % demoSteps?.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const ActivePanel = stepPanels?.[activeStep];

  return (
    <section id="demo" className="py-14 px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
          <Sparkles size={12} />
          Live Demo
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">
          See How It Works
        </h2>
        <p className="text-slate-300 text-base sm:text-lg max-w-xl mx-auto">
          From a single request to a complete, export-ready design — watch the AI workflow in action.
        </p>
      </div>
      <div className="max-w-4xl mx-auto">
        {/* Step indicators */}
        <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2 gap-1">
          {demoSteps?.map((step, i) => {
            const Icon = step?.icon;
            const isActive = i === activeStep;
            const isDone = i < activeStep;
            return (
              <button
                key={step?.id}
                onClick={() => setActiveStep(i)}
                className={`flex-1 min-w-[80px] flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all duration-200 ${
                  isActive ? step?.bg + ' border ' + step?.border : 'hover:bg-white/5'
                }`}
              >
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                  isDone ? 'bg-green-500/20 border border-green-500/30' : isActive ? step?.bg +' border ' + step?.border : 'bg-surface-3 border border-border'
                }`}>
                  {isDone ? (
                    <Check size={14} className="text-green-400" />
                  ) : (
                    <Icon size={14} className={isActive ? step?.color : 'text-slate-500'} />
                  )}
                </div>
                <span className={`text-[10px] sm:text-xs font-semibold text-center leading-tight ${isActive ? step?.color : isDone ? 'text-green-400' : 'text-slate-500'}`}>
                  {step?.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="h-0.5 bg-surface-3 rounded-full mb-6 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-amber-400 rounded-full transition-all duration-500"
            style={{ width: `${((activeStep + 1) / demoSteps?.length) * 100}%` }}
          />
        </div>

        {/* Active panel */}
        <div className="rounded-3xl border border-border bg-card p-5 sm:p-6 min-h-[220px] transition-all duration-300">
          <div className="flex items-center gap-2 mb-4">
            {(() => {
              const step = demoSteps?.[activeStep];
              const Icon = step?.icon;
              return (
                <>
                  <div className={`w-7 h-7 rounded-lg ${step?.bg} border ${step?.border} flex items-center justify-center`}>
                    <Icon size={14} className={step?.color} />
                  </div>
                  <span className={`text-sm font-bold ${step?.color}`}>{step?.label}</span>
                  {activeStep > 0 && (
                    <span className="ml-auto text-xs text-green-400 flex items-center gap-1">
                      <Check size={11} />
                      Previous step complete
                    </span>
                  )}
                </>
              );
            })()}
          </div>
          <ActivePanel />
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            disabled={activeStep === 0}
            className="px-4 py-2 rounded-xl text-sm font-medium text-slate-400 hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Previous
          </button>
          <div className="flex gap-1.5">
            {demoSteps?.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeStep ? 'bg-primary w-4' : 'bg-surface-3'}`}
              />
            ))}
          </div>
          {activeStep < demoSteps?.length - 1 ? (
            <button
              onClick={() => setActiveStep((s) => s + 1)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-primary hover:text-amber-300 transition-colors flex items-center gap-1"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <Link
              href="/sign-up-login-screen"
              className="btn-primary flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold"
            >
              <Sparkles size={13} />
              Try It Free
              <ArrowRight size={13} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
